import React, { useRef, useState } from 'react';
import { useSceneScrollTimings } from '../../hooks/useSceneScrollTimings';
import { GlassCard } from '../shared/GlassCard';
import { Button } from '../ui/button';
import { Building2, PiggyBank, Home, Car, ArrowRight } from 'lucide-react';

/**
 * Scene 2 - The Fix
 * 
 * Shows 4 strategy cards in horizontal scroll layout.
 * Desktop: Horizontal scroll with ScrollTrigger pin
 * Mobile: Vertical stack
 * 
 * High contrast: White text on red-navy-gold gradient
 */

const strategies = [
  {
    id: 0,
    title: "S-Corp Election",
    savings: "$8,000 – $15,000 / yr",
    description: "Reduce self-employment tax",
    icon: Building2,
    color: "teal",
  },
  {
    id: 1,
    title: "Retirement Optimization",
    savings: "$20,000 – $60,000 / yr",
    description: "Maximize tax-advantaged contributions",
    icon: PiggyBank,
    color: "gold",
  },
  {
    id: 2,
    title: "Home Office Deduction",
    savings: "$3,000 – $8,000 / yr",
    description: "Claim legitimate workspace expenses",
    icon: Home,
    color: "teal",
  },
  {
    id: 3,
    title: "Vehicle Strategy",
    savings: "$5,000 – $12,000 / yr",
    description: "Optimize business use deductions",
    icon: Car,
    color: "amber",
  },
];

export const Scene2Fix = ({ onSceneChange, onProgressUpdate }) => {
  const sectionRef = useRef(null);
  const [activeCard, setActiveCard] = useState(0);

  // Track scene entry/exit
  useSceneScrollTimings(sectionRef, {
    start: 'top center',
    end: 'bottom center',
    onEnter: () => {
      if (onSceneChange) onSceneChange(2);
    },
    onUpdate: (progress) => {
      if (onProgressUpdate) onProgressUpdate(25 + progress * 30); // 25-55% of journey
      // Update active card based on scroll progress
      const cardIndex = Math.floor(progress * strategies.length);
      setActiveCard(Math.min(cardIndex, strategies.length - 1));
    },
  });

  return (
    <section
      id="fix"
      ref={sectionRef}
      className="relative min-h-screen flex items-center justify-center bg-gradient-fix overflow-hidden pt-20"
      data-testid="fix-section"
    >
      {/* Noise texture */}
      <div className="absolute inset-0 opacity-[0.02] pointer-events-none bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzMDAiIGhlaWdodD0iMzAwIj48ZmlsdGVyIGlkPSJhIj48ZmVUdXJidWxlbmNlIGJhc2VGcmVxdWVuY3k9Ii43NSIgc3RpdGNoVGlsZXM9InN0aXRjaCIvPjwvZmlsdGVyPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbHRlcj0idXJsKCNhKSIvPjwvc3ZnPg==')] bg-repeat" />

      <div className="max-w-7xl mx-auto w-full px-6 sm:px-8 lg:px-12 py-20">
        {/* Header */}
        <div className="text-center mb-16">
          <h2
            className="text-3xl sm:text-4xl lg:text-6xl font-display font-semibold text-white leading-tight mb-4"
            style={{ textShadow: '0 2px 20px rgba(0,0,0,0.6)' }}
            data-testid="fix-title"
          >
            Strategies don't just save taxes —
            <br />
            they build confidence.
          </h2>
          <p
            className="text-lg sm:text-xl text-white/80 font-sans max-w-2xl mx-auto"
            style={{ textShadow: '0 1px 12px rgba(0,0,0,0.5)' }}
            data-testid="fix-intro"
          >
            The right moves turn leaks into gains.
          </p>
        </div>

        {/* Strategy Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {strategies.map((strategy, index) => {
            const Icon = strategy.icon;
            const isActive = activeCard === index;

            return (
              <GlassCard
                key={strategy.id}
                hover
                className={`fix-card p-6 transition-all duration-500 transform hover:scale-105 hover:-translate-y-2 ${
                  isActive ? 'ring-2 ring-teal-400/50 shadow-[0_0_30px_rgba(17,197,217,0.3)]' : ''
                }`}
                testId={`strategy-card-${strategy.id}`}
              >
                {/* Icon */}
                <div className="mb-4">
                  <div className="inline-flex p-3 rounded-lg bg-white/10">
                    <Icon
                      className="w-8 h-8 text-teal-300"
                      strokeWidth={1.5}
                      data-testid={`strategy-card-icon-${strategy.id}`}
                    />
                  </div>
                </div>

                {/* Title */}
                <h3
                  className="text-xl font-display font-semibold text-white mb-2"
                  style={{ textShadow: '0 1px 8px rgba(0,0,0,0.5)' }}
                  data-testid="strategy-card-title"
                >
                  {strategy.title}
                </h3>

                {/* Description */}
                <p
                  className="text-sm text-white/70 font-sans mb-4"
                  data-testid="strategy-card-description"
                >
                  {strategy.description}
                </p>

                {/* Savings */}
                <div className="mb-6">
                  <p
                    className={`text-2xl font-mono font-bold ${
                      strategy.color === 'teal'
                        ? 'text-teal-300'
                        : strategy.color === 'gold'
                        ? 'text-gold'
                        : 'text-amber'
                    }`}
                    style={{
                      textShadow: `0 0 20px ${strategy.color === 'teal' ? 'rgba(17,197,217,0.4)' : strategy.color === 'gold' ? 'rgba(201,162,39,0.4)' : 'rgba(255,184,105,0.4)'}`,
                    }}
                    data-testid="strategy-card-savings"
                  >
                    {strategy.savings}
                  </p>
                </div>

                {/* CTA Button */}
                <Button
                  size="sm"
                  onClick={() => {
                    console.log(`Strategy CTA clicked: ${strategy.title}`);
                    window.alert(`📋 ${strategy.title}\n\nLearn the step-by-step process to implement this strategy.`);
                  }}
                  className="w-full bg-white/10 hover:bg-white/20 text-white border border-white/20 hover:border-white/30 transition-all duration-300 hover:shadow-[0_0_20px_rgba(17,197,217,0.4)] transform hover:scale-105"
                  data-testid="strategy-card-cta-button"
                >
                  See Steps
                  <ArrowRight className="ml-2 w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Button>
              </GlassCard>
            );
          })}
        </div>

        {/* Subtext */}
        <p
          className="text-center text-sm sm:text-base text-white/60 font-sans mt-12"
          style={{ textShadow: '0 1px 8px rgba(0,0,0,0.5)' }}
          data-testid="fix-subtext"
        >
          Each strategy is CPA-approved & 100% legal.
        </p>
      </div>
    </section>
  );
};